import { RequestAbortError } from '../../api/client.js';
import { ORIGIN_MANUAL, ORIGIN_THIS_MAC, WIFI_VAULT_LIMIT, type MacWifiBridge, type MacWifiResult, type WifiNetwork } from '../../api/wifi-types.js';
import { getMobileBridge } from '../../platform/mobile-bridge.js';
import type { SessionController } from '../../session/controller.js';
import { confirmModal, h, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import { createWifiSend } from '../components/wifi-send.js';
import { createProductionSync } from '../sync/device-sync-deps.js';
import type { DeviceSync } from '../sync/device-sync.js';
import { wifiErrorText, wifiListIsStale } from '../wifi-text.js';

export interface RedesOptions {
  /** Bluetooth + receipts engine for «Enviar al aparato». Tests inject one; production builds it. */
  sync?: DeviceSync;
  /** The Mac app's Keychain bridge. `null` hides «Añadir desde este Mac». */
  macWifi?: MacWifiBridge | null;
}

type Panel =
  | { kind: 'closed' }
  | { kind: 'mac-list'; state: 'loading' } | { kind: 'mac-list'; state: 'failed'; message: string } | { kind: 'mac-list'; state: 'ready'; ssids: string[] }
  | { kind: 'mac-confirm'; ssid: string; reading: boolean; notice?: string }
  | { kind: 'manual'; ssid: string; locked: boolean; notice?: string };

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: false });
const MAC_FALLBACK = 'No se pudo leer la información de macOS. Puedes escribir la contraseña.';

function ssidProblem(ssid: string): string | null {
  const bytes = encoder.encode(ssid).length;
  if (bytes < 1) return 'Escribe el nombre de la red.';
  if (bytes > 32) return 'El nombre de la red admite hasta 32 bytes.';
  if (/\p{Cc}/u.test(ssid) || decoder.decode(encoder.encode(ssid)) !== ssid) return 'El nombre no puede tener caracteres de control.';
  return null;
}
function pskProblem(psk: string): string | null {
  if (psk.length === 0) return 'Escribe la contraseña: las redes abiertas no se guardan.';
  if (!/^[\x20-\x7e]*$/u.test(psk)) return 'La contraseña solo admite caracteres ASCII imprimibles, sin tildes ni eñes.';
  if (psk.length < 8 || psk.length > 63) return 'La contraseña debe tener entre 8 y 63 caracteres.';
  return null;
}
const byPriority = (a: WifiNetwork, b: WifiNetwork) => a.priority - b.priority || a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const sortNetworks = (list: readonly WifiNetwork[]) => [...list].sort(byPriority);

export function createRedesView(controller: Pick<SessionController, 'getApi'>, options: RedesOptions = {}): View {
  const api = controller.getApi();
  const mac: MacWifiBridge | null = options.macWifi !== undefined ? options.macWifi : (getMobileBridge() ? null : window.asisDesktop?.wifi ?? null);
  const sync = options.sync ?? createProductionSync(api, () => null, { usb: false });
  const lifetime = new AbortController();
  let alive = true;
  let busy = false;
  let load: 'loading' | 'ready' | 'error' = 'loading';
  let loadError = '';
  let networks: WifiNetwork[] = [];
  let editing: string | null = null;
  let panel: Panel = { kind: 'closed' };
  let macToken = 0;
  let shareNew = true;
  let loadCtl: AbortController | null = null;
  let pendingFocus: string | null = null;
  let pendingIndex = 0;
  let editor: { id: string; el: HTMLFormElement; input: HTMLInputElement; error: HTMLElement } | null = null;

  const root = h('section', { class: 'view view-redes', id: 'view-redes' });
  root.appendChild(renderHeader('redes'));
  const body = h('div', { class: 'section-body' });
  root.appendChild(body);

  const notice = h('p', { class: 'sync-status', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
  const alert = h('p', { class: 'auth-error', role: 'alert', hidden: true, 'data-role': 'list-alert' });
  const retry = h('button', { class: 'btn ghost', type: 'button', hidden: true }, ['Reintentar']);
  const list = h('ul', { class: 'device-list network-list', 'aria-label': 'Redes Wi-Fi guardadas', tabindex: '-1' });
  const empty = h('p', { class: 'card-sub', hidden: true }, ['Todavía no has guardado ninguna red. Añade la de tu casa u oficina para enviarla a tus aparatos sin volver a teclear la contraseña.']);
  const addMac = h('button', { class: 'btn ghost', type: 'button' }, ['Añadir desde este Mac']);
  const addManual = h('button', { class: 'btn ghost', type: 'button' }, ['Añadir manualmente']);
  const panelHost = h('div', { class: 'wifi-add-panel' });
  const listCard = h('section', { class: 'card', 'aria-labelledby': 'wifi-list-title' }, [
    h('h2', { class: 'card-title', id: 'wifi-list-title' }, ['Tus redes guardadas']),
    h('p', { class: 'card-sub' }, ['Se guardan cifradas en tu cuenta de ASIS. La primera es la preferida. Las que marques con «Compartir con mis dispositivos» aparecen elegidas por defecto al enviar; nada llega a un aparato sin que tú lo pidas.']),
    h('div', { class: 'form-row' }, [...(mac ? [addMac] : []), addManual]),
    panelHost, notice, alert, retry, empty, list,
  ]);
  const send = createWifiSend({ sync });
  body.append(listCard, send.el);

  function say(text: string): void { notice.textContent = text; alert.textContent = ''; alert.hidden = true; }
  function fail(text: string): void { notice.textContent = ''; alert.textContent = text; alert.hidden = !text; }

  // ---- list ----
  function renderList(): void {
    if (!alive) return;
    retry.hidden = load !== 'error'; retry.disabled = busy;
    if (load === 'error') { fail(loadError); }
    empty.hidden = !(load === 'ready' && networks.length === 0);
    list.hidden = load !== 'ready';
    list.setAttribute('aria-busy', load === 'loading' ? 'true' : 'false');
    if (load === 'loading') { list.replaceChildren(h('li', { class: 'empty' }, ['Cargando tus redes…'])); list.hidden = false; return; }
    if (load !== 'ready') { list.replaceChildren(); return; }
    // A rebuild must not drop a keyboard that is already on a row control (a reload after a conflict rebuilds twice).
    const held = document.activeElement instanceof HTMLElement && list.contains(document.activeElement) ? document.activeElement : null;
    if (held?.dataset.focus && !pendingFocus) {
      pendingFocus = held.dataset.focus;
      pendingIndex = Math.max(0, [...list.children].indexOf(held.closest('li') as Element));
    }
    list.replaceChildren(...networks.map((network, index) => row(network, index)));
    addMac.disabled = addManual.disabled = busy;
    if (pendingFocus && !busy) {
      const key = pendingFocus;
      pendingFocus = null;
      // Do not steal the keyboard from a field the user moved to while a request was pending.
      const active = document.activeElement;
      if (!active || active === document.body || active === list || list.contains(active) || !root.contains(active)) focusEquivalent(key, pendingIndex)?.focus();
    }
  }

  /** The same control; else another one in the same row; else the row now at that position; else the list itself. */
  function focusEquivalent(key: string, index: number): HTMLElement | null {
    const owner = key.split(':')[0];
    const usable = (selector: string) => list.querySelector<HTMLElement>(`${selector}:not([disabled])`);
    const neighbour = list.children[Math.min(index, list.children.length - 1)];
    return usable(`[data-focus="${key}"]`) ?? usable(`button[data-focus^="${owner}:"]`)
      ?? neighbour?.querySelector<HTMLElement>('button:not([disabled])') ?? usable('button') ?? list;
  }
  function wantFocus(id: string, control: string): void {
    pendingFocus = `${id}:${control}`;
    pendingIndex = Math.max(0, networks.findIndex(item => item.id === id));
  }

  function row(network: WifiNetwork, index: number): HTMLLIElement {
    const name = h('div', { class: 'device-name' }); name.textContent = network.ssid;
    const sub = h('div', { class: 'device-sub' }, [`Prioridad ${index + 1} de ${networks.length}`]);
    const origin = network.origin ? h('span', { class: 'tag' }) : null;
    if (origin) origin.textContent = network.origin;
    const share = h('input', { type: 'checkbox', 'data-focus': `${network.id}:share`, 'aria-label': `Compartir ${network.ssid} con mis dispositivos` });
    share.checked = network.shared; share.disabled = busy;
    share.addEventListener('change', () => { wantFocus(network.id, 'share'); void changeShared(network, share.checked); });
    const action = (key: string, label: string, text: string, onClick: () => void, extra = '') => {
      const button = h('button', { class: `btn ghost small ${extra}`.trim(), type: 'button', 'data-focus': `${network.id}:${key}`, 'aria-label': `${label} ${network.ssid}` }, [text]);
      button.addEventListener('click', onClick);
      return button;
    };
    const up = action('up', 'Subir prioridad de', 'Subir', () => { wantFocus(network.id, 'up'); void move(network.id, -1); });
    const down = action('down', 'Bajar prioridad de', 'Bajar', () => { wantFocus(network.id, 'down'); void move(network.id, 1); });
    up.disabled = busy || index === 0; down.disabled = busy || index === networks.length - 1;
    const edit = action('edit', 'Cambiar la contraseña de', 'Cambiar contraseña', () => { wantFocus(network.id, 'edit'); openEditor(network); });
    const remove = action('delete', 'Eliminar', 'Eliminar', () => { wantFocus(network.id, 'delete'); void confirmRemove(network); }, 'danger');
    edit.disabled = remove.disabled = busy;
    const li = h('li', { class: 'device-row network-row', dataset: { network: network.id } }, [
      h('div', { class: 'device-main' }, [name, sub]),
      ...(origin ? [h('div', { class: 'device-chips' }, [origin])] : []),
      h('label', { class: 'field inline network-share' }, [share, h('span', {}, ['Compartir con mis dispositivos'])]),
      h('div', { class: 'device-actions' }, [up, down, edit, remove]),
    ]);
    if (editor && editor.id === network.id) li.append(editor.el);
    return li;
  }

  async function run(task: (signal: AbortSignal) => Promise<void>, reload = false): Promise<boolean> {
    if (busy || !alive) return false;
    busy = true; renderList(); renderPanel(false);
    try { await task(lifetime.signal); return true; }
    catch (error) {
      if (!alive || error instanceof RequestAbortError) return false;
      fail(wifiErrorText(error));
      if (reload || wifiListIsStale(error)) { busy = false; await loadList(); }
      return false;
    } finally { if (alive) { busy = false; renderList(); renderPanel(false); } }
  }
  const merge = (next: WifiNetwork) => {
    networks = sortNetworks(networks.some(item => item.id === next.id) ? networks.map(item => (item.id === next.id ? next : item)) : [...networks, next]);
    send.setNetworks(networks);
  };

  async function loadList(): Promise<void> {
    loadCtl?.abort();
    const ctl = new AbortController(); loadCtl = ctl;
    const signal = AbortSignal.any([lifetime.signal, ctl.signal]);
    const recovering = load === 'error';
    if (load !== 'ready') { load = 'loading'; renderList(); }
    try {
      const result = await api.listWifiNetworks(signal);
      if (!alive || signal.aborted) return;
      networks = sortNetworks(result.networks); load = 'ready'; loadError = '';
      if (recovering) fail('');
      send.setNetworks(networks);
    } catch (error) {
      if (!alive || signal.aborted || error instanceof RequestAbortError) return;
      load = 'error'; loadError = wifiErrorText(error);
    }
    renderList();
  }

  async function move(id: string, direction: -1 | 1): Promise<void> {
    const from = networks.findIndex(item => item.id === id); const to = from + direction;
    if (from < 0 || to < 0 || to >= networks.length) return;
    const moved = networks[from]!;
    const order = [...networks]; [order[from], order[to]] = [order[to]!, order[from]!];
    const current = networks.map(item => item.priority);
    // Distinct priorities only need to trade places; ties are renumbered so the new order sticks.
    const targets = current.every((value, index) => index === 0 || value > current[index - 1]!) ? current : order.map((_, index) => index);
    const changes = order.map((item, index) => ({ item, priority: targets[index]! })).filter(change => change.item.priority !== change.priority);
    await run(async signal => {
      for (const change of changes) merge((await api.updateWifiNetwork(change.item.id, change.item.revision, { priority: change.priority }, signal)).network);
      say(`«${moved.ssid}» tiene ahora la prioridad ${to + 1}.`);
    }, true);
  }

  async function changeShared(network: WifiNetwork, value: boolean): Promise<void> {
    const done = await run(async signal => {
      merge((await api.updateWifiNetwork(network.id, network.revision, { shared: value }, signal)).network);
      say(value ? `«${network.ssid}» se ofrecerá por defecto al enviar redes.` : `«${network.ssid}» ya no se ofrece por defecto al enviar redes.`);
    });
    if (!done) renderList();
  }

  async function confirmRemove(network: WifiNetwork): Promise<void> {
    if (busy) return;
    const yes = await confirmModal({ title: 'Eliminar red', body: `Vas a eliminar «${network.ssid}» de tu cuenta. Los aparatos que ya la tengan guardada la conservan.`, confirmLabel: 'Eliminar', danger: true });
    if (!yes || !alive) return;
    const current = networks.find(item => item.id === network.id);
    if (!current) return;
    await run(async signal => {
      await api.deleteWifiNetwork(current.id, current.revision, signal);
      networks = networks.filter(item => item.id !== current.id); send.setNetworks(networks);
      if (editing === current.id) closeEditor();
      say(`Red «${current.ssid}» eliminada.`);
    });
  }

  // ---- password editor (inline, one at a time) ----
  function closeEditor(): void {
    if (editor) editor.input.value = '';
    editor = null; editing = null;
  }
  function openEditor(network: WifiNetwork): void {
    if (busy) return;
    closeEditor();
    const input = h('input', { class: 'input', type: 'password', autocomplete: 'off', maxlength: '63', spellcheck: 'false', 'aria-label': `Nueva contraseña de ${network.ssid}` });
    const error = h('p', { class: 'auth-error', role: 'alert', hidden: true });
    const save = h('button', { class: 'btn primary', type: 'submit' }, ['Guardar contraseña']);
    const cancel = h('button', { class: 'btn ghost', type: 'button' }, ['Cancelar']);
    const el = h('form', { class: 'form-stack network-edit', 'aria-label': `Cambiar la contraseña de ${network.ssid}` }, [
      h('label', { class: 'field' }, [h('span', {}, ['Nueva contraseña (8 a 63 caracteres)']), input]), error, h('div', { class: 'form-row' }, [save, cancel]),
    ]);
    editor = { id: network.id, el, input, error }; editing = network.id;
    cancel.addEventListener('click', () => { closeEditor(); wantFocus(network.id, 'edit'); renderList(); });
    el.addEventListener('submit', event => {
      event.preventDefault();
      if (busy) return;
      let psk = input.value; input.value = '';
      const problem = pskProblem(psk);
      if (problem) { psk = ''; error.textContent = problem; error.hidden = false; input.focus(); return; }
      error.hidden = true;
      const current = networks.find(item => item.id === network.id);
      if (!current) { psk = ''; return; }
      void run(async signal => {
        try { merge((await api.updateWifiNetwork(current.id, current.revision, { psk }, signal)).network); } finally { psk = ''; }
        closeEditor(); wantFocus(current.id, 'edit'); say(`Contraseña de «${current.ssid}» actualizada.`);
      }).then(done => { if (!done && alive && editor?.id === current.id) editor.input.focus(); });
    });
    renderList();
    input.focus();
  }

  // ---- adding ----
  async function saveNetwork(ssid: string, psk: string, origin: string): Promise<boolean> {
    const existed = networks.some(item => item.ssid === ssid);
    if (!existed && networks.length >= WIFI_VAULT_LIMIT) { psk = ''; fail('Ya tienes 10 redes guardadas. Elimina alguna para añadir otra.'); return false; }
    return run(async signal => {
      try { merge((await api.createWifiNetwork({ ssid, psk, origin, shared: shareNew }, signal)).network); } finally { psk = ''; }
      say(existed ? `Contraseña de «${ssid}» actualizada.` : `Red «${ssid}» guardada.`);
    });
  }

  function closePanel(): void {
    macToken++;
    panel = { kind: 'closed' };
    renderPanel();
  }
  function setPanel(next: Panel): void { panel = next; renderPanel(); }

  function shareField(): HTMLElement {
    const box = h('input', { type: 'checkbox' });
    box.checked = shareNew;
    box.addEventListener('change', () => { shareNew = box.checked; });
    return h('label', { class: 'field inline' }, [box, h('span', {}, ['Compartir con mis dispositivos'])]);
  }

  function renderPanel(rebuild = true): void {
    if (!alive) return;
    if (!rebuild) {
      for (const control of panelHost.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input,button')) {
        if (control.dataset.keep === 'true') continue;
        control.disabled = busy || (panel.kind === 'mac-confirm' && panel.reading);
      }
      return;
    }
    const wasFocus = panelHost.contains(document.activeElement);
    panelHost.replaceChildren();
    addMac.setAttribute('aria-expanded', String(panel.kind.startsWith('mac')));
    addManual.setAttribute('aria-expanded', String(panel.kind === 'manual'));
    if (panel.kind === 'closed') { if (wasFocus) addManual.focus(); return; }
    const close = h('button', { class: 'btn ghost', type: 'button' }, ['Cerrar']);
    close.addEventListener('click', () => { if (panel.kind === 'mac-confirm' && panel.reading) { void cancelRead(); return; } closePanel(); addManual.focus(); });
    if (panel.kind === 'mac-list') {
      const current = panel;
      const box = h('div', { class: 'form-stack wifi-panel' }, [h('h3', { class: 'card-title' }, ['Redes guardadas en este Mac'])]);
      if (current.state === 'loading') box.append(h('p', { class: 'card-sub', role: 'status' }, ['Leyendo la lista de redes de este Mac…']));
      else if (current.state === 'failed') {
        const text = h('p', { class: 'auth-error', role: 'alert' }); text.textContent = current.message;
        box.append(text, h('p', { class: 'card-sub' }, ['Puedes añadir la red a mano.']));
      } else if (current.ssids.length === 0) box.append(h('p', { class: 'card-sub' }, ['Este Mac no tiene redes Wi-Fi guardadas. Puedes añadir una a mano.']));
      else {
        box.append(h('p', { class: 'card-sub' }, ['Elige la red. Aún no se lee ninguna contraseña.']));
        box.append(h('ul', { class: 'wireless-choices mac-ssid-list', 'aria-label': 'Redes guardadas en este Mac' }, current.ssids.map(ssid => {
          const button = h('button', { class: 'btn ghost', type: 'button' });
          button.textContent = ssid;
          button.addEventListener('click', () => setPanel({ kind: 'mac-confirm', ssid, reading: false }));
          return h('li', {}, [button]);
        })));
      }
      const manual = h('button', { class: 'btn ghost', type: 'button' }, ['Escribir una red a mano']);
      manual.addEventListener('click', () => setPanel({ kind: 'manual', ssid: '', locked: false }));
      box.append(h('div', { class: 'form-row' }, [manual, close]));
      panelHost.append(box);
    } else if (panel.kind === 'mac-confirm') {
      const current = panel;
      const title = h('h3', { class: 'card-title' }); title.textContent = `Leer la contraseña de «${current.ssid}»`;
      const read = h('button', { class: 'btn primary', type: 'button' }, [current.reading ? 'Esperando a macOS…' : 'Leer del llavero']);
      read.disabled = current.reading || busy;
      const typed = h('button', { class: 'btn ghost', type: 'button' }, ['Escribir la contraseña']);
      typed.disabled = current.reading;
      typed.addEventListener('click', () => { if (!current.reading) setPanel({ kind: 'manual', ssid: current.ssid, locked: true }); });
      const stop = h('button', { class: 'btn ghost', type: 'button', hidden: !current.reading, 'data-keep': 'true' }, ['Cancelar lectura']);
      stop.addEventListener('click', () => { void cancelRead(); });
      // The click that reads the Keychain must itself call the bridge: the Mac only answers a real user gesture.
      read.addEventListener('click', () => { void readKeychain(current.ssid, read); });
      const text = current.notice ? h('p', { class: 'card-sub', role: 'status' }) : null;
      if (text) text.textContent = current.notice ?? '';
      panelHost.append(h('div', { class: 'form-stack wifi-panel' }, [
        title,
        h('p', { class: 'card-sub' }, ['macOS te pedirá autorización para leer esta clave del llavero y puede pedirte la contraseña de tu Mac. Se la das a macOS, nunca a ASIS. Después se guarda cifrada en tu cuenta de ASIS.']),
        shareField(), ...(text ? [text] : []),
        h('div', { class: 'form-row' }, [read, typed, stop, ...(current.reading ? [] : [close])]),
      ]));
      if (wasFocus && !current.reading) read.focus();
    } else {
      const current = panel;
      const ssidInput = h('input', { class: 'input', type: 'text', autocomplete: 'off', spellcheck: 'false', required: true, 'aria-label': 'Nombre de la red Wi-Fi', ...(current.locked ? { readonly: true } : {}) });
      ssidInput.value = current.ssid;
      const pskInput = h('input', { class: 'input', type: 'password', autocomplete: 'off', maxlength: '63', spellcheck: 'false', required: true, 'aria-label': 'Contraseña de la red Wi-Fi' });
      const problem = h('p', { class: 'auth-error', role: 'alert', hidden: true });
      const save = h('button', { class: 'btn primary', type: 'submit' }, ['Guardar red']);
      const form = h('form', { class: 'form-stack wifi-panel', 'aria-label': 'Añadir una red Wi-Fi' }, [
        h('h3', { class: 'card-title' }, [current.locked ? 'Escribe la contraseña' : 'Añadir una red a mano']),
        ...(current.notice ? [(() => { const note = h('p', { class: 'card-sub', role: 'status' }); note.textContent = current.notice ?? ''; return note; })()] : []),
        h('label', { class: 'field' }, [h('span', {}, ['Nombre de la red (hasta 32 bytes)']), ssidInput]),
        h('label', { class: 'field' }, [h('span', {}, ['Contraseña (8 a 63 caracteres)']), pskInput]),
        shareField(), problem, h('div', { class: 'form-row' }, [save, close]),
      ]);
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (busy) return;
        const ssid = ssidInput.value; let psk = pskInput.value; pskInput.value = '';
        const issue = ssidProblem(ssid) ?? pskProblem(psk);
        if (issue) { psk = ''; problem.textContent = issue; problem.hidden = false; (ssidProblem(ssid) ? ssidInput : pskInput).focus(); return; }
        problem.hidden = true;
        void saveNetwork(ssid, psk, ORIGIN_MANUAL).then(saved => { if (saved && alive) closePanel(); });
        psk = '';
      });
      panelHost.append(form);
      (current.locked ? pskInput : ssidInput).focus();
    }
  }

  async function openMacList(): Promise<void> {
    if (!mac || busy) return;
    const token = ++macToken;
    setPanel({ kind: 'mac-list', state: 'loading' });
    let result: MacWifiResult<{ ssids: string[] }>;
    try { result = await mac.listSaved(); } catch { result = { ok: false, code: 'failed', message: MAC_FALLBACK }; }
    if (!alive || token !== macToken) return;
    setPanel(result.ok ? { kind: 'mac-list', state: 'ready', ssids: result.ssids } : { kind: 'mac-list', state: 'failed', message: result.message || MAC_FALLBACK });
  }

  async function readKeychain(ssid: string, trigger: HTMLButtonElement): Promise<void> {
    if (!mac || busy || (panel.kind === 'mac-confirm' && panel.reading)) return;
    const token = ++macToken;
    trigger.disabled = true;
    // Called synchronously from the click: no await may come before the bridge call.
    const pending = mac.readPassword(ssid);
    setPanel({ kind: 'mac-confirm', ssid, reading: true, notice: 'Esperando la autorización de macOS. Puede tardar hasta un minuto.' });
    let result: MacWifiResult<{ psk: string }>;
    try { result = await pending; } catch { result = { ok: false, code: 'failed', message: MAC_FALLBACK }; }
    if (!alive || token !== macToken) return;
    if (!result.ok) {
      if (result.code === 'busy') { setPanel({ kind: 'mac-confirm', ssid, reading: false, notice: result.message || MAC_FALLBACK }); return; }
      setPanel({ kind: 'manual', ssid, locked: true, notice: `${result.message || MAC_FALLBACK}` });
      return;
    }
    let psk: string | null = result.psk;
    setPanel({ kind: 'mac-confirm', ssid, reading: false });
    const saved = await saveNetwork(ssid, psk, ORIGIN_THIS_MAC);
    psk = null;
    if (!alive || token !== macToken) return;
    if (saved) closePanel();
  }

  async function cancelRead(): Promise<void> {
    if (!mac || !(panel.kind === 'mac-confirm' && panel.reading)) return;
    const ssid = panel.ssid;
    macToken++;
    try { await mac.cancel(); } catch { /* the main process also stops on navigation */ }
    if (!alive) return;
    setPanel({ kind: 'mac-confirm', ssid, reading: false, notice: 'Lectura cancelada. No se ha guardado nada.' });
  }

  addMac.addEventListener('click', () => { void openMacList(); });
  addManual.addEventListener('click', () => { if (!busy) setPanel({ kind: 'manual', ssid: '', locked: false }); });
  retry.addEventListener('click', () => { void loadList(); });

  void sync.refreshAccount();
  void loadList();
  return {
    el: root,
    destroy() {
      alive = false; macToken++;
      loadCtl?.abort(); lifetime.abort();
      if (mac && panel.kind === 'mac-confirm' && panel.reading) { try { void Promise.resolve(mac.cancel()).catch(() => undefined); } catch { /* best effort */ } }
      closeEditor();
      for (const input of root.querySelectorAll<HTMLInputElement>('input[type="password"],input[type="text"]')) input.value = '';
      panelHost.replaceChildren(); networks = [];
      send.destroy?.(); sync.cancel(); sync.destroy();
    },
    update() { if (!busy && load !== 'loading') void loadList(); },
  };
}
